import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Switch, FormControlLabel, Chip,
} from '@mui/material';
import { Add, Edit, Delete, Tv } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { cabinService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '', description: '', isActive: true };

const CabinManagement = () => {
  const navigate = useNavigate();
  const [cabins, setCabins] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await cabinService.getAll();
      setCabins(res.data);
    } catch (err) {
      console.error('Failed to load cabins:', err);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name, description: item.description || '', isActive: item.isActive });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await cabinService.update(editingItem.id, formData);
      } else {
        await cabinService.create(formData);
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Failed to save cabin:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await cabinService.delete(id);
      setDeleteConfirmId(null);
      loadData();
    } catch (err) {
      console.error('Failed to delete cabin:', err);
      setDeleteConfirmId(null);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Cabin Name', width: 180, renderCell: (p) => <strong>{p.value}</strong> },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200, renderCell: (p) => p.value || '—' },
    {
      field: 'isActive', headerName: 'Status', width: 120,
      renderCell: (p) => (
        <Chip
          label={p.value ? 'Active' : 'Inactive'}
          color={p.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)} title="Edit">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(params.row.id)} title="Delete">
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar
        title="OPD Cabin Management"
        onBack={() => navigate('/dashboard')}
        actions={
          <Button
            color="inherit"
            startIcon={<Tv />}
            onClick={() => navigate('/admin/cabin-assignment')}
            sx={{ mr: 1, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 2, px: 2 }}
          >
            Cabin Assignment
          </Button>
        }
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" fontWeight={600} color="text.secondary">
              {cabins.length} cabin{cabins.length !== 1 ? 's' : ''} configured
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Cabin
            </Button>
          </Box>

          <DataGrid
            rows={cabins}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Cabin' : 'Add Cabin'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cabin Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoFocus
                  placeholder="e.g. Cabin A"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g. Ground floor, left wing"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Cabin</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this cabin? All its assignments will also be removed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirmId)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CabinManagement;
